import type { ApiResponse } from '@psspl/types';

import { ApiClientError, isApiErrorCode } from './api-error';
import { webEnv } from '../lib/env';

/**
 * Typed `fetch` wrapper for the portal's API boundary.
 *
 * Screens never call `fetch`. They call a service function, which calls this.
 * Responsibilities kept here so no service re-implements them:
 *
 *  - prefixing the base path (`webEnv.apiBaseUrl`, `/api` in dev and prod)
 *  - attaching the access token, if the auth layer holds one
 *  - refresh-on-401 per `docs/api.md` §4.1: on `TOKEN_EXPIRED` / `TOKEN_INVALID`,
 *    one silent refresh and one retry, then a session-expired signal
 *  - unwrapping the `ApiResponse` envelope from `docs/api.md` section 2
 *  - normalising every failure into `ApiClientError`
 *  - timeouts, so a hung request cannot leave a skeleton on screen forever
 *
 * `credentials: 'include'` is required: the refresh token travels only in the
 * `HttpOnly` `phub_refresh` cookie (`docs/api.md` §4.1). The access token is sent as
 * `Authorization: Bearer` from memory.
 */
export const REQUEST_TIMEOUT_MS = 15_000;

/**
 * Endpoints whose 401 is the answer itself — a Microsoft identity Prisco does
 * not know, a dead refresh cookie — so they never trigger a refresh or the
 * session-expired signal. Logout is not in the list: §4.5 says an expired access
 * token on logout means "refresh, then log out".
 */
const AUTH_PATHS = ['/auth/microsoft', '/auth/refresh'] as const;

/** 401 codes that a refresh can cure (§4.1). */
const REFRESHABLE_CODES: ReadonlySet<string> = new Set(['TOKEN_EXPIRED', 'TOKEN_INVALID']);

/** 401 codes that mean the session is gone. */
const SESSION_CODES: ReadonlySet<string> = new Set([
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
]);

type TokenReader = () => string | null;
type SessionRefresher = () => Promise<boolean>;
type SessionExpiredHandler = () => void;

let tokenReader: TokenReader = () => null;
let sessionRefresher: SessionRefresher | null = null;
let sessionExpiredHandler: SessionExpiredHandler | null = null;

/** Single-flight: ten requests failing at once share one refresh call. */
let refreshInFlight: Promise<boolean> | null = null;

/** Registered once by the auth service. Kept as a hook to avoid an import cycle. */
export function setAccessTokenReader(reader: TokenReader): void {
  tokenReader = reader;
}

/**
 * Registered by the auth service. Resolves `true` when a new access token is in
 * place and the failed request is worth retrying.
 */
export function setSessionRefresher(refresher: SessionRefresher): void {
  sessionRefresher = refresher;
}

/**
 * Registered by the auth provider, so an expired session drops the whole app to
 * signed-out exactly once instead of every screen handling it.
 *
 * Returns an unregister function for effect cleanup.
 */
export function setSessionExpiredHandler(handler: SessionExpiredHandler): () => void {
  sessionExpiredHandler = handler;

  return () => {
    if (sessionExpiredHandler === handler) {
      sessionExpiredHandler = null;
    }
  };
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
  readonly timeoutMs?: number | undefined;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = webEnv.apiBaseUrl.replace(/\/$/u, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  let url = `${base}${suffix}`;

  if (query !== undefined) {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    }

    const serialised = params.toString();

    if (serialised.length > 0) {
      url = `${url}?${serialised}`;
    }
  }

  return url;
}

/** A file upload: sent as `multipart/form-data`, with the boundary the browser picks. */
function isMultipart(body: unknown): body is FormData {
  return body instanceof FormData;
}

function toHeaders(body: unknown): Headers {
  const headers = new Headers({ Accept: 'application/json' });

  if (body !== undefined && !isMultipart(body)) {
    headers.set('Content-Type', 'application/json');
  }

  const token = tokenReader();

  if (token !== null && token.length > 0) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

function isAuthPath(path: string): boolean {
  return AUTH_PATHS.some((authPath) => path === authPath || path.startsWith(`${authPath}?`));
}

async function refreshSession(): Promise<boolean> {
  if (sessionRefresher === null) {
    return false;
  }

  refreshInFlight ??= sessionRefresher()
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

async function errorCodeFrom(response: Response): Promise<string | null> {
  const payload = await parseBody(response);

  if (typeof payload === 'object' && payload !== null && 'error' in payload) {
    const error = (payload as { readonly error?: unknown }).error;

    if (typeof error === 'object' && error !== null && 'code' in error) {
      const code = (error as { readonly code?: unknown }).code;
      return typeof code === 'string' ? code : null;
    }
  }

  return null;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (text.length === 0) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function failureFrom(status: number, payload: unknown, fallbackMessage: string): ApiClientError {
  if (typeof payload === 'object' && payload !== null && 'error' in payload) {
    const candidate = (payload as { readonly error?: unknown }).error;

    if (typeof candidate === 'object' && candidate !== null) {
      const error = candidate as {
        readonly code?: unknown;
        readonly message?: unknown;
        readonly details?: unknown;
        readonly requestId?: unknown;
      };

      const message =
        typeof error.message === 'string' && error.message.length > 0
          ? error.message
          : fallbackMessage;

      return new ApiClientError({
        // An unknown code is normalised rather than cast, so screens only ever
        // branch on the shared vocabulary.
        code:
          typeof error.code === 'string' && isApiErrorCode(error.code)
            ? error.code
            : 'INTERNAL_ERROR',
        message,
        status,
        details: Array.isArray(error.details) ? error.details : [],
        requestId: typeof error.requestId === 'string' ? error.requestId : null,
      });
    }
  }

  return ApiClientError.from(statusToCode(status), fallbackMessage, status);
}

/** Envelope-less failure (proxy error page, empty body) mapped to the shared vocabulary. */
function statusToCode(status: number): string {
  switch (status) {
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 429:
      return 'RATE_LIMITED';
    case 502:
    case 503:
    case 504:
      return 'SERVICE_UNAVAILABLE';
    default:
      return 'INTERNAL_ERROR';
  }
}

/**
 * The transport. Owns timeout, abort wiring and the refresh-on-401 retry.
 *
 * `attempt` is internal: the retry after a refresh is attempt 1, and a 401 on that
 * attempt ends the session instead of refreshing again. There is no loop.
 */
async function send(path: string, options: RequestOptions, attempt = 0): Promise<Response> {
  const { method = 'GET', query, body, signal, timeoutMs = REQUEST_TIMEOUT_MS } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  const onExternalAbort = (): void => {
    controller.abort();
  };

  signal?.addEventListener('abort', onExternalAbort);

  let response: Response;

  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: toHeaders(body),
      // `null` rather than `undefined`: `exactOptionalPropertyTypes` rejects an
      // explicit `undefined` on RequestInit.body.
      body: body === undefined ? null : isMultipart(body) ? body : JSON.stringify(body),
      credentials: 'include',
      signal: controller.signal,
    });
  } catch {
    if (signal?.aborted === true) {
      throw new ApiClientError({
        code: 'INTERNAL_ERROR',
        message: 'The request was cancelled.',
        status: 0,
      });
    }

    throw new ApiClientError({
      code: 'SERVICE_UNAVAILABLE',
      message: controller.signal.aborted
        ? 'The service took too long to respond. Please try again.'
        : 'The service is not reachable. Check your connection and try again.',
      status: 0,
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onExternalAbort);
  }

  if (response.status === 401 && !isAuthPath(path)) {
    // Read the code from a clone so the caller can still consume the body.
    const code = await errorCodeFrom(response.clone());

    if (attempt === 0 && code !== null && REFRESHABLE_CODES.has(code) && (await refreshSession())) {
      return send(path, options, attempt + 1);
    }

    if (code === null || SESSION_CODES.has(code)) {
      sessionExpiredHandler?.();
    }
  }

  return response;
}

export async function request<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
  const response = await send(path, options);
  const payload = await parseBody(response);

  if (!response.ok) {
    throw failureFrom(
      response.status,
      payload,
      'The request could not be completed. Please try again.',
    );
  }

  if (typeof payload === 'object' && payload !== null && 'success' in payload) {
    const envelope = payload as ApiResponse<TData>;

    if (envelope.success) {
      return envelope.data;
    }

    throw failureFrom(response.status, payload, 'The request could not be completed.');
  }

  return payload as TData;
}

/**
 * Same transport as `request`, but hands back the raw `Response`.
 *
 * Needed for non-JSON payloads (the access-log CSV export). Keeps base-path
 * prefixing, auth headers, refresh, timeouts and error normalisation in one place
 * instead of letting a service call `fetch` directly.
 */
export async function requestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const response = await send(path, options);

  if (!response.ok) {
    throw failureFrom(
      response.status,
      await parseBody(response),
      'The request could not be completed. Please try again.',
    );
  }

  return response;
}

export const httpClient = {
  get: <TData>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<TData> =>
    request<TData>(path, { ...options, method: 'GET' }),

  post: <TData>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method'>,
  ): Promise<TData> => request<TData>(path, { ...options, method: 'POST', body }),

  patch: <TData>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method'>,
  ): Promise<TData> => request<TData>(path, { ...options, method: 'PATCH', body }),

  /** A `FormData` body is sent as `multipart/form-data`; anything else as JSON. */
  put: <TData>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method'>,
  ): Promise<TData> => request<TData>(path, { ...options, method: 'PUT', body }),

  delete: <TData>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<TData> => request<TData>(path, { ...options, method: 'DELETE' }),
} as const;
